import { Box, Button, CircularProgress, type Modal, Tab, Tabs, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { useDebounce } from './debounce'

export interface SpendControlCategory {
  id: string
  name: string
  description: string
  partner: string
  ruleId: string
}

type Hook<Data, Payload = undefined> = (...args: Payload extends undefined ? [] : [Payload]) => {
  isLoading: boolean
  data: Data | undefined
}

export interface SpendControlTableProps {
  useRule: SpendRuleProps['useRule']
  useCategories: Hook<SpendControlCategory[], { partner: string | undefined }>
  parsers: SpendRuleProps['parsers']
  slots: {
    Table: DataTable
    Modal: Modal
  }
  partner: string | undefined
}

type DataTableColumns = typeof columns

interface DataTableProps {
  rows: SpendControlCategory[]
  columns: DataTableColumns
  loading: boolean | undefined
  onRowClick: (row: SpendControlCategory) => void
}

export type DataTable = React.ComponentType<DataTableProps>

interface ModalProps {
  open: boolean
  onRequestClose: () => void
  heading: string | undefined
}

// biome-ignore lint/suspicious/noRedeclare: ignore existing biome errors
export type Modal = React.ComponentType<ModalProps>

export const SpendControlTable = ({ useRule, slots: { Table, Modal }, useCategories, parsers, partner }: SpendControlTableProps) => {
  const { data: categoryData = [], isLoading: categoryIsLoading } = useCategories({ partner })
  const [selectedRuleId, setSelectedRuleId] = useState<string | undefined>()

  return (
    <>
      <Modal onRequestClose={() => setSelectedRuleId(undefined)} open={Boolean(selectedRuleId)} heading="Spend control rule">
        {selectedRuleId && <SpendRule parsers={parsers} id={selectedRuleId} useRule={useRule} />}
      </Modal>
      <Table rows={categoryData} columns={columns} loading={categoryIsLoading} onRowClick={(row) => setSelectedRuleId(row.ruleId)} />
    </>
  )
}

export interface SpendControlRule {
  id: string
  name: string
  description: string
  expression: string
}

interface SpendRuleProps {
  id: string
  useRule: Hook<SpendControlRule, { id: string }>
  onLoaded?: () => void
  parsers: {
    mid: (expression: string) => string[]
    mcc: (expression: string) => string[]
  }
}

const SpendRule = ({ id, useRule, parsers }: SpendRuleProps) => {
  const [filter, setFilter] = useState('')
  const [tab, setTab] = useState('0')
  const { data, isLoading } = useRule({ id })

  const handleFilter = useDebounce((search: string) => {
    setFilter(search)
  }, 200)

  const handleChangeTab = (tab) => {
    setFilter('')
    setTab(tab)
  }

  if (isLoading) return <CircularProgress />
  if (!data) return <Typography>No data</Typography>

  const mids = parsers.mid(data.expression)
  const mccs = parsers.mcc(data.expression)

  const filteredMids = mids.filter((mid) => mid.startsWith(filter))
  const filteredMCCs = mccs.filter((mcc) => mcc.startsWith(filter))

  const handleCopy = (value: string) => window.navigator.clipboard.writeText(value)

  return (
    <Box display="flex" gap={2.5} flexDirection="column">
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
            <Typography>{data.name}</Typography>
            <Typography fontSize={14} color="text.secondary">
              {data.description}
            </Typography>
          </Box>
          <Box display="flex" flexWrap="nowrap" gap={1}>
            <Button onClick={() => handleCopy(data.expression)} sx={{ whiteSpace: 'pre' }} size="small">
              Copy expression
            </Button>
            <Button onClick={() => handleCopy(filteredMids.join(','))} sx={{ whiteSpace: 'pre' }} size="small">
              Copy {filteredMids.length} MIDs
            </Button>
          </Box>
        </Box>
      </Box>
      <Tabs value={tab} onChange={(_, tab) => handleChangeTab(tab)}>
        <Tab value={0} label="MIDs" />
        <Tab value={1} label="MCCs" />
      </Tabs>
      <TogglableTab value={tab} tabIndex={0}>
        <TextField onChange={(event) => handleFilter(event.target.value)} size="small" label={`Search MIDs (${mids.length})`} />
        <Box
          sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', overflow: 'scroll', maxHeight: '200px', gap: 0.5, padding: 1 }}
        >
          {filteredMids.map((mid) => (
            // biome-ignore lint/correctness/useJsxKeyInIterable: <explanation>
            <Typography display="flex" textAlign="center" fontSize={14} color="text.secondary">
              {mid}
            </Typography>
          ))}
        </Box>
      </TogglableTab>
      {/** biome-ignore lint/a11y/noPositiveTabindex: <explanation> */}
      <TogglableTab value={tab} tabIndex={1}>
        <TextField onChange={(event) => handleFilter(event.target.value)} size="small" label={`Search MCCs (${mccs.length})`} />
        <Box
          sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', overflow: 'scroll', maxHeight: '200px', gap: 0.5, padding: 1 }}
        >
          {filteredMCCs.map((mid) => (
            // biome-ignore lint/correctness/useJsxKeyInIterable: <explanation>
            <Typography display="flex" textAlign="center" fontSize={14} color="text.secondary">
              {mid}
            </Typography>
          ))}
        </Box>
      </TogglableTab>
    </Box>
  )
}

interface TabPanelProps {
  value: string
  tabIndex: number
  children: React.ReactNode
}

const TogglableTab = ({ value, tabIndex, children }: TabPanelProps) => {
  if (value.toString() === tabIndex?.toString()) {
    return <>{children}</>
  }

  return null
}

const columns = [
  {
    headerName: 'Name',
    field: 'name',
  },
  {
    headerName: 'Description',
    field: 'description',
  },
  {
    headerName: 'Partner',
    field: 'partner',
  },
]
